// Banner exibido no detail quando há downgrade agendado — Story 3.5 AC7.

import { CalendarClock } from 'lucide-react';
import { toast } from 'sonner';
import { formatBR, parseLocalDate } from '@control-center/shared';

import { PillButton } from '../clients/PillButton';
import { useCancelPendingDowngrade } from '../../hooks/use-subscriptions';
import { extractErrorMessage } from '../../lib/errors';

interface PendingDowngradeBannerProps {
  subscriptionId: string;
  /** YYYY-MM-DD do scheduledFor. */
  scheduledFor: string;
  /** Nome do plano alvo, se disponível na UI. Senão UUID. */
  targetPlanLabel: string;
  /**
   * Distingue fim de CORTESIA de downgrade agendado. O texto padrão descreve
   * um rebaixamento; numa cortesia isso conta a história errada — o cliente foi
   * SUBIDO de graça e vai voltar ao plano que já pagava.
   */
  changeType?: 'downgrade' | 'migration' | 'courtesy_end';
}

export function PendingDowngradeBanner({
  subscriptionId,
  scheduledFor,
  targetPlanLabel,
  changeType = 'downgrade',
}: PendingDowngradeBannerProps): JSX.Element {
  const cancelMutation = useCancelPendingDowngrade();
  const isCourtesy = changeType === 'courtesy_end';

  async function handleCancel(): Promise<void> {
    const ok = window.confirm(
      isCourtesy
        ? 'Encerrar a cortesia agora? O cliente volta imediatamente ao plano que ele paga.'
        : 'Cancelar o downgrade agendado? O cliente continua no plano atual e na próxima cobrança.',
    );
    if (!ok) return;
    try {
      await cancelMutation.mutateAsync(subscriptionId);
      toast.success(isCourtesy ? 'Cortesia encerrada.' : 'Downgrade cancelado.');
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Falha ao cancelar.'));
    }
  }

  return (
    <div className="flex items-start gap-3 rounded-[12px] border border-warn/40 bg-warn/5 p-4">
      <CalendarClock size={18} strokeWidth={1.8} className="mt-0.5 shrink-0 text-warn" />
      <div className="flex-1">
        <div className="text-[13px] font-medium text-ink">
          {isCourtesy ? 'Cortesia ativa' : 'Downgrade agendado'}
        </div>
        <p className="mt-1 text-[12px] text-muted">
          {isCourtesy ? (
            <>
              Cliente está em plano superior de cortesia. Em{' '}
              <strong>{formatBR(parseLocalDate(scheduledFor))}</strong> volta para{' '}
              <strong>{targetPlanLabel}</strong>, pelo mesmo valor que já paga. Nenhuma cobrança
              extra foi gerada.
            </>
          ) : (
            <>
              Aplica em <strong>{formatBR(parseLocalDate(scheduledFor))}</strong> para{' '}
              <strong>{targetPlanLabel}</strong>. Cliente mantém acesso ao plano atual até lá.
            </>
          )}
        </p>
      </div>
      <PillButton
        type="button"
        onClick={() => void handleCancel()}
        disabled={cancelMutation.isPending}
      >
        {cancelMutation.isPending ? 'Cancelando…' : 'Cancelar agendamento'}
      </PillButton>
    </div>
  );
}
